import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { useLang } from "../i18n";
import { openCookiePreferences } from "../lib/consent";
import SeoLinks from "./SeoLinks";
import { PAYMENT_LOGOS } from "../lib/payments";
import { SITE_URL } from "./Seo";
import { LOGO } from "./maquette/Header";

const SHOPS = [
  ["Smarti Arlon", "Rue des Déportés 3, 6700 Arlon", "+32 63 50 03 54", "/atelier/store-arlon.webp"],
  ["Smarti Oberpallen", "2 Arelerstrooss, 8552 Uewerpallen (LU)", "+352 27 225 395", "/atelier/store-oberpallen.webp"],
];
const PRESS = [["/atelier/rtl.webp", "RTL info"], ["/atelier/shootlux.png", "Shootlux"], ["/atelier/sudinfo.png", "Sudinfo"]];

function FooterLocal() {
  return (
    <div className="mx-auto mb-4 grid max-w-7xl gap-4 lg:grid-cols-[1fr_1fr_1.1fr]" data-testid="footer-local">
      {SHOPS.map(([n, a, tel, img]) => (
        <div key={n} className="flex items-center gap-4 rounded-[24px] bg-white p-3 shadow-[0_8px_30px_rgba(16,24,40,.06)]" data-testid={`footer-shop-${n.split(" ")[1]}`}>
          <img src={img} alt={`Boutique ${n}`} loading="lazy" className="h-20 w-24 shrink-0 rounded-[16px] object-cover" />
          <div className="min-w-0 text-sm">
            <div className="font-semibold text-ink">{n}</div>
            <div className="text-ink-soft">{a}</div>
            <a href={`tel:${tel.replace(/\s/g, "")}`} className="text-[13px] font-semibold text-[#0073FF]">{tel}</a>
          </div>
        </div>
      ))}
      <div className="flex flex-col justify-center rounded-[24px] bg-white p-5 shadow-[0_8px_30px_rgba(16,24,40,.06)]" data-testid="footer-press">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-faint">Le groupe Smarti dans la presse</div>
        <div className="mt-3 flex flex-wrap items-center gap-6 opacity-80">{PRESS.map(([s, a]) => <img key={a} src={s} alt={a} loading="lazy" className="h-8 w-auto max-w-[110px] object-contain" />)}</div>
      </div>
    </div>
  );
}

const ORIGIN = SITE_URL || (typeof window !== "undefined" ? window.location.origin : "https://smarti-renew.be");
const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Smarti Renew",
  url: ORIGIN,
  inLanguage: "fr-BE",
  potentialAction: { "@type": "SearchAction", target: { "@type": "EntryPoint", urlTemplate: `${ORIGIN}/catalogue?q={search_term_string}` }, "query-input": "required name=search_term_string" },
};

const CERT_LOGOS = "https://customer-assets-4nw71qhi.emergentagent.net/job_smarti-renew-store/artifacts/lv1d3t2k_Artisan-PhoneCheck.webp";
const UPS_LOGO = "https://customer-assets-4nw71qhi.emergentagent.net/job_smarti-renew-store/artifacts/tl10isir_UPS-Logo%20%281%29.webp";
const BPOST_LOGO = "https://customer-assets-4nw71qhi.emergentagent.net/job_smarti-renew-store/artifacts/s6wbbmbv_Bpost_2010_%28logo%29.svg.webp";
const POSTNL_LOGO = "https://customer-assets-4nw71qhi.emergentagent.net/job_smarti-renew-store/artifacts/ywfyyhs9_Postnl.png";

const COLS = [
  {
    title: "Boutique",
    links: [
      { l: "Smartphones", to: "/categorie/smartphones" },
      { l: "Tablettes", to: "/categorie/tablettes" },
      { l: "Accessoires", to: "/categorie/accessoires" },
      { l: "Tout le catalogue", to: "/catalogue" },
    ],
  },
  {
    title: "Services",
    links: [
      { l: "Reprise & rachat", to: "/reprise" },
      { l: "Location de smartphone", to: "/location" },
      { l: "Trouver mon smartphone", to: "/trouver-mon-smartphone" },
      { l: "Cartes cadeaux", to: "/cartes-cadeaux" },
      { l: "Nos grades", to: "/nos-grades" },
      { l: "Livraison & retours", to: "/livraison" },
      { l: "Suivi de commande", to: "/compte" },
      { l: "Nos boutiques", to: "/boutiques" },
    ],
  },
  {
    title: "Aide",
    links: [
      { l: "Blog", to: "/blog" },
      { l: "FAQ", to: "/faq" },
      { l: "Contact", to: "/contact" },
      { l: "À propos", to: "/a-propos" },
    ],
  },
];

const LEGAL = [
  { l: "Mentions légales", to: "/mentions-legales" },
  { l: "CGV", to: "/conditions-generales-de-vente" },
  { l: "Confidentialité", to: "/politique-de-confidentialite" },
  { l: "Cookies", to: "/politique-cookies" },
  { l: "Retours", to: "/politique-retour" },
  { l: "Garantie", to: "/garantie" },
  { l: "Rétractation", to: "/formulaire-retractation" },
  { l: "Paiement", to: "/paiement" },
  { l: "Livraison", to: "/livraison" },
  { l: "Location", to: "/conditions-location" },
  { l: "Reprise", to: "/conditions-reprise" },
  { l: "Accessibilité", to: "/accessibilite" },
  { l: "Contact", to: "/contact" },
  { l: "Litiges en ligne (ODR)", to: "https://ec.europa.eu/consumers/odr", ext: true },
];

export default function Footer() {
  const { t } = useLang();
  const home = ["/", "/a-propos"].includes(useLocation().pathname);
  return (
    <footer className="mt-16 px-3 pb-10 lg:px-5" data-testid="site-footer">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(JSON_LD).replace(/</g, "\\u003c") }} />
      {!home && <FooterLocal />}
      <div className="mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-white shadow-[0_8px_30px_rgba(16,24,40,.06)]">
        <div className="px-7 py-12 lg:px-14 lg:py-14">
          {/* Top: brand + columns */}
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-[1.7fr_1fr_1fr_1fr]">
            <div>
              <img src={LOGO} alt="Smarti Renew" className="h-10 w-auto" data-testid="footer-logo" />
              <p className="mt-2 text-xs font-medium text-ink-faint" data-testid="footer-legal-company">
                by Smarti Group SRL · BCE BE 1019.893.830
              </p>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-ink-soft">
                {t("Smartphones reconditionnés premium (iPhone, Samsung) testés, certifiés et garantis 36 mois. Reprise de votre ancien appareil et retrait en boutique à Arlon.")}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-5 text-sm">
                <a href="https://smarti-repair.be" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-ink-soft transition-colors hover:text-ink">Smarti Repair <ArrowUpRight className="h-3.5 w-3.5" /></a>
                <a href="https://smarti-glass.be" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-ink-soft transition-colors hover:text-ink">Smarti Glass <ArrowUpRight className="h-3.5 w-3.5" /></a>
              </div>
            </div>

            {COLS.map((c) => (
              <div key={c.title}>
                <div className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-[#0073FF]">— {t(c.title)}</div>
                <ul className="space-y-3">
                  {c.links.map((x) => (
                    <li key={x.to}>
                      <Link to={x.to} className="text-sm text-ink-soft transition-colors hover:text-ink" data-testid={`footer-link-${x.to}`}>{t(x.l)}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* SEO internal-linking band */}
          <div className="mt-12">
            <SeoLinks />
          </div>

          {/* Trust logos strip */}
          <div className="mt-12 grid items-start gap-8 border-t border-black/5 pt-8 sm:grid-cols-3" data-testid="footer-trust">
            <div>
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-ink-faint">{t("Certifications")}</div>
              <img src={CERT_LOGOS} alt="Artisanat Certifié · Phonecheck Certified" width={200} height={56} className="h-14 w-auto object-contain" data-testid="footer-cert-logos" loading="lazy" />
            </div>
            <div>
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-ink-faint">{t("Livraison")}</div>
              <div className="flex flex-wrap items-center gap-5" data-testid="footer-carriers">
                <img src={UPS_LOGO} alt="UPS" width={64} height={36} className="h-9 w-auto object-contain" loading="lazy" />
                <img src={BPOST_LOGO} alt="bpost" width={37} height={20} className="h-5 w-auto object-contain" loading="lazy" />
                <img src={POSTNL_LOGO} alt="PostNL" width={60} height={32} className="h-8 w-auto object-contain" loading="lazy" />
              </div>
            </div>
            <div>
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-ink-faint">{t("Paiement")}</div>
              <div className="flex flex-wrap items-center gap-2" data-testid="footer-payments">
                {PAYMENT_LOGOS.map((p) => (
                  <img key={p.name} src={p.logo} alt={p.name} title={p.name} width={40} height={26} loading="lazy" decoding="async"
                    className="h-[26px] w-10 object-contain" data-testid={`footer-pay-${p.name}`} />
                ))}
              </div>
              <a href="https://help.almapay.com/" target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-ink-soft underline underline-offset-2 hover:text-ink" data-testid="footer-alma-claims">{t("Réclamation paiement Alma")}</a>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-10 flex flex-col items-start justify-between gap-4 border-t border-black/5 pt-6 sm:flex-row sm:items-center">
            <span className="text-sm text-ink-soft" data-testid="footer-copyright">© {new Date().getFullYear()} Smarti Group SRL. {t("Tous droits réservés.")}</span>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              {LEGAL.map((x) => (
                x.ext ? (
                  <a key={x.to} href={x.to} target="_blank" rel="noreferrer" className="text-sm text-ink-soft transition-colors hover:text-ink" data-testid={`footer-legal-${x.to}`}>{t(x.l)}</a>
                ) : (
                  <Link key={x.to} to={x.to} className="text-sm text-ink-soft transition-colors hover:text-ink" data-testid={`footer-legal-${x.to}`}>{t(x.l)}</Link>
                )
              ))}
              <button onClick={openCookiePreferences} className="text-sm text-ink-soft transition-colors hover:text-ink" data-testid="footer-manage-cookies">{t("Gérer les cookies")}</button>
              <a href="https://wa.me/3263500354" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-ink-soft transition-colors hover:text-ink" data-testid="footer-whatsapp"><MessageCircle className="h-4 w-4 text-eco" /> WhatsApp</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
